/**
 * Menu — the overflow control (`⋯`) whose absence is why every action in this app is a
 * top-level button (UX cleanup, 2026-08-25).
 *
 * The walkthrough that produced `KLQ-20260825-007` found four controls on a settings row,
 * five on a level panel and thirteen on one task. None of that was a series of bad calls:
 * `components/` offered a Button and nothing else, so *every* action an author wanted to
 * offer had to be visible, and a row with four capabilities drew four buttons. This is the
 * component that makes "available, not shouted" expressible.
 *
 * **This file is now the trigger and nothing else.** The list — the portal, the placement,
 * the arrow keys, the item model, the submenu — moved into `MenuPopup.tsx` when
 * `KLQ-20260825-006` landed, exactly as this header used to promise it would: the context
 * menu is *a second way to open this popup and this item model*, never a second menu
 * component. What is left here is the one thing a context menu has no use for — a button that
 * says a menu is available and a rect to line it up with.
 *
 * **Why a menu is hand-written at all**, since that argument still has to survive here:
 * `ChipSelect` argues, at length and correctly, that a hand-rolled listbox is a pile of
 * accessibility to get quietly wrong and that the platform already ships the control. That
 * argument does not transfer. There is no native menu. `<select>` is a *value*, and a menu is
 * a list of *commands* — binding Delete to an option would make Delete a thing a form
 * submits. `<details>`/`<summary>` gives open and closed and nothing else: no outside click,
 * no arrow keys, no return of focus.
 *
 * **It has no copy of its own.** Every word arrives as a prop, the way `ChipSelect` and
 * `Slicer` take theirs — so this file adds no keys to the `app` namespace and cannot disagree
 * with a screen's own vocabulary.
 *
 * OWNING SPEC: `KLQ-20260825-006` §C.
 */

import { MoreHorizontal } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useCallback, useId, useRef, useState } from "react";

import { drawnActions, MenuPopup } from "./MenuPopup";
import type { MenuCloseReason, MenuItem } from "./MenuPopup";
import styles from "./Menu.module.css";

export type {
  MenuAction,
  MenuAnchor,
  MenuCloseReason,
  MenuItem,
  MenuSeparator,
  MenuTone,
} from "./MenuPopup";
export { drawnActions, drawnItems, isSeparator, MenuPopup } from "./MenuPopup";

export interface MenuProps {
  /** The trigger's accessible name — *More actions*, already translated. */
  readonly label: string;
  readonly items: readonly MenuItem[];
  /** Drawn beside the glyph. Without it the trigger is icon-only and `label` is its whole
      accessible name. */
  readonly triggerLabel?: string | undefined;
  /** Which edge of the popup lines up with which edge of the trigger. Row actions sit at the
      right of their row, so `end` is the default. */
  readonly align?: "start" | "end" | undefined;
  readonly disabled?: boolean | undefined;
}

export function Menu({
  label,
  items,
  triggerLabel,
  align = "end",
  disabled = false,
}: MenuProps) {
  const menuId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  /**
   * The trigger's box, captured when the menu opens rather than read while rendering. The
   * popup positions itself from a value, so the value has to be one — measuring during render
   * would make placement a function of when React happened to run.
   */
  const [open, setOpen] = useState<{
    rect: DOMRect;
    entry: "first" | "last";
  } | null>(null);

  const actions = drawnActions(items);

  const close = useCallback((reason: MenuCloseReason) => {
    setOpen(null);
    // See `MenuCloseReason`: stepping back and finishing both belong at the trigger; only a
    // Tab or a click elsewhere leaves focus where the person is putting it.
    if (reason !== "moved-on") trigger.current?.focus();
  }, []);

  function openWith(entry: "first" | "last"): void {
    const rect = trigger.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setOpen({ rect, entry });
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openWith("first");
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      openWith("last");
    }
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={[
          styles.trigger,
          triggerLabel === undefined ? styles.iconOnly : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={disabled || actions.length === 0}
        aria-haspopup="menu"
        aria-expanded={open !== null}
        aria-controls={open === null ? undefined : menuId}
        onKeyDown={onTriggerKeyDown}
        onClick={() => {
          if (open === null) openWith("first");
          else close("dismissed");
        }}
      >
        <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
        {triggerLabel === undefined ? (
          <span className="sr-only">{label}</span>
        ) : (
          <span className={styles.triggerLabel}>{triggerLabel}</span>
        )}
      </button>

      {open === null ? null : (
        <MenuPopup
          label={label}
          id={menuId}
          items={items}
          anchor={{ kind: "element", rect: open.rect, align }}
          entry={open.entry}
          onClose={close}
        />
      )}
    </>
  );
}
